import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapPin, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddressAutocomplete({ label, value, onChange, testId }) {
  const [query, setQuery] = useState(value?.address || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (value?.address && value.address !== query) setQuery(value.address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.address]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (q.trim().length < 3) { setResults([]); return; }
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/geocode/search`, { params: { q } });
        setResults(data);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const pick = (r) => {
    const short = r.display_name.split(",").slice(0, 3).join(",").trim();
    onChange({ address: short, full_address: r.display_name, lat: r.lat, lon: r.lon });
    setQuery(short);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange({ ...(value || {}), address: e.target.value }); search(e.target.value); }}
          onFocus={() => query.length >= 3 && setOpen(true)}
          placeholder="Start typing an address..."
          className="w-full h-11 pl-10 pr-10 rounded-lg border border-slate-300 focus:border-[#081537] focus:ring-2 focus:ring-[#081537]/10 outline-none text-sm"
        />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
        ) : (
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-40 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl max-h-72 overflow-auto" data-testid={`${testId}-results`}>
          {results.map((r) => (
            <button
              key={r.place_id}
              type="button"
              onClick={() => pick(r)}
              data-testid={`${testId}-result`}
              className={cn("w-full text-left px-4 py-3 hover:bg-slate-50 flex gap-3 items-start border-b border-slate-100 last:border-0")}
            >
              <MapPin className="w-4 h-4 text-[#081537] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-slate-700">{r.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
